import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "react-use";
import { useParams } from "react-router";
import truncate from "lodash/truncate";
import { addYears, startOfDay } from "date-fns";
import { Box } from "@mui/material";
import { useAccountsId } from "@rentablo/api/apiv1";
import { AccountsHeader, BitpandaV1Banner, CurrencyToggle } from "@components";
import { CardPageLayout, CardHeading } from "@rentablo/components";
import AccountPreviewIcon from "@components/Accounts/AccountPreviewCard/AccountPreviewIcon";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData";
import { MandatorConditional, useMandatorVisibility } from "@helpers";
import { useRangeSelectControl } from "@hooks";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { ChartControlsProvider } from "@components/Chart/ChartControls";
import { AssetTypeBreakdown } from "../components/DepotsDashboard/AssetTypeBreakdown";
import {
  DepotsDashboardChart,
  DepotsDashboardContent,
} from "../components/DepotsDashboard";
import {
  AssetClassFilterChip,
  AssetClassFilterProvider,
} from "../components/DepotsDashboard/assetClassFilter";
import { DepotPageActions } from "./components/DepotPageActions";

export const DepotPage = () => {
  const { t } = useTranslation();
  const { depotId } = useParams<"depotId">();
  const parsedDepotId = Number.parseInt(depotId!, 10);
  const isComparisonSelectorVisible = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });
  // The asset class cards filter the whole page, chart included; without them
  // (kpmg-germany) the chart keeps its own asset type checkboxes.
  const showAssetClassCards = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });
  const bookingsRangeProps = useRangeSelectControl({
    firstDate: new Date(),
    id: "depot-bookings",
  });
  const [statistic, setStatistic] = useState<InvestmentsChartDataStatistic>();
  const [showHistoric = false, setShowHistoric] = useLocalStorage(
    "show-historic-depot",
    false
  );

  const { data: account } = useAccountsId({
    id: parsedDepotId,
  });
  const { globalCurrency, getDisplayCurrency, selectedCurrencyView } =
    useCurrencyDisplay();
  const hasForeignCurrency = Boolean(
    account?.currency &&
      account.currency.toUpperCase() !== globalCurrency.toUpperCase()
  );

  const currency = getDisplayCurrency(account?.currency);
  const depotChartFirstDate = startOfDay(addYears(new Date(), -1));

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: t("breadcrumbs.home"),
      to: "/",
    },
    {
      text: t("breadcrumbs.depotsAccounts"),
      to: "/depots-accounts",
    },
    {
      text: truncate(account?.name),
    },
  ];

  const handleShowHistoricChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setShowHistoric(event.target.checked);

  return (
    <AssetClassFilterProvider storageKey="asset-class-filter-depot">
      <CardPageLayout
        data-testid="single-depot-page"
        breadcrumbs={breadcrumbs}
        banner={<BitpandaV1Banner account={account} />}
        cardHeading={
          <CardHeading
            title={
              <AccountsHeader
                title={account?.name ?? ""}
                subtitle={account?.bankName || t("accountBox.noBank")}
                nativeCurrency={
                  hasForeignCurrency ? account?.currency : undefined
                }
                icon={
                  account ? (
                    <AccountPreviewIcon
                      isReference={Boolean(account.depotIds?.length ?? 0)}
                      iconUrl={account.iconUrl}
                      type={account.type}
                      size={48}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            }
            actions={
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                gap={2}
              >
                <DepotPageActions />
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <CurrencyToggle
                    storageKey="currency-toggle-depot"
                    originalCurrency={account?.currency}
                  />
                </Box>
              </Box>
            }
            actionsInline
            below={
              <>
                {/* Only with a currency to switch: an empty slot would still
                    add the heading's gap above the chart. */}
                {hasForeignCurrency && (
                  <Box
                    sx={{
                      display: { xs: "flex", md: "none" },
                      justifyContent: "flex-start",
                      width: "100%",
                    }}
                  >
                    <CurrencyToggle
                      storageKey="currency-toggle-depot"
                      originalCurrency={account?.currency}
                    />
                  </Box>
                )}
                <AssetClassFilterChip />
              </>
            }
          />
        }
        cardChildren={
          <ChartControlsProvider
            rangeId="depot"
            rangeFirstDate={depotChartFirstDate}
            controls={{
              showChartType: true,
              showCompare: isComparisonSelectorVisible,
              showAssetTypes: !showAssetClassCards,
            }}
          >
            <Box>
              <DepotsDashboardChart
                accountIds={[parsedDepotId]}
                onStatistic={setStatistic}
                currency={currency}
              />
            </Box>
          </ChartControlsProvider>
        }
      >
        <MandatorConditional hideFor={["kpmg-germany"]}>
          <AssetTypeBreakdown
            accountIds={[parsedDepotId]}
            currency={currency}
          />
        </MandatorConditional>
        {statistic && (
          <DepotsDashboardContent
            accountIds={[parsedDepotId]}
            statistic={statistic}
            currency={
              selectedCurrencyView === "global" ? globalCurrency : undefined
            }
            summaryCurrency={currency}
            showHistoric={showHistoric}
            onShowHistoricChange={handleShowHistoricChange}
            bookingsRangeProps={bookingsRangeProps}
            onMobileSettingsReset={(tab) => {
              if (tab === "investments") setShowHistoric(false);
              else
                bookingsRangeProps.selectInterval(
                  bookingsRangeProps.defaultIntervalType
                );
            }}
          />
        )}
      </CardPageLayout>
    </AssetClassFilterProvider>
  );
};
